import { useTranslation } from 'react-i18next'

import { Badge, Card, Skeleton } from '@/design-system/primitives'
import { ScreenLinkCard } from '@/features/admin/components/ScreenLinkCard'
import { formatClock, useCountdown } from '@/features/voting/countdown'
import { formatTime } from '@/lib/datetime'

import { useScreenPreview } from '../api'
import { OverlayBanner } from './Segments'
import { LinkWithQr, SectionHead, useQrTargets } from './shared'

/**
 * L'écran de la salle, vu du conducteur.
 *
 * Ce que la télé affiche en ce moment — l'étape à l'affiche, la suite, les
 * votes annoncés, le départage, les titres proclamés, le gâteau — relu à la
 * cadence de l'écran lui-même. L'adresse de l'écran (avec sa clé) n'apparaît
 * qu'à qui règle l'édition ; les autres la reçoivent de l'administration.
 */
export function ScreenTab({
  eventId,
  timezone,
  can,
}: {
  eventId: string | undefined
  timezone: string
  can: (permission: string) => boolean
}) {
  const { t, i18n } = useTranslation()
  const preview = useScreenPreview(eventId, true)
  const qr = useQrTargets(eventId, can('event.event.read'))
  const screen = preview.data
  const locale = i18n.language
  const programTarget = qr.data?.find((target) => target.key === 'page:program')

  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <SectionHead
          title={t('conductor.cockpit.screen.now')}
          hint={t('conductor.cockpit.screen.nowHint')}
        />
        {/* L'affichage posé par le MC (pronostic, gagnant) passe par-dessus
            tout le reste : on le montre en premier, avec de quoi le retirer. */}
        <OverlayBanner eventId={eventId} overlay={screen?.overlay} can={can} />
        {screen && !screen.overlay && can('program.screen.control') ? (
          <p className="text-caption text-content-muted">{t('conductor.segments.nothingOnScreen')}</p>
        ) : null}
        {preview.isPending ? <Skeleton className="h-40 w-full" /> : null}
        {preview.isError ? (
          <p className="text-sm text-state-danger">{t('state.errorTitle')}</p>
        ) : null}
        {screen ? (
          <Card className="space-y-4 border-champagne-400/30 p-4 sm:p-6">
            <p className="overline break-words text-champagne-400">{screen.event_name}</p>

            {screen.cake ? (
              <div>
                <Badge tone="ember">{t('conductor.cockpit.screen.cake')}</Badge>
                <p className="mt-2 break-words font-display text-heading-md text-content-primary">
                  {screen.cake.name}
                </p>
                {screen.cake.message ? (
                  <p className="mt-1 break-words text-sm text-content-secondary">
                    {screen.cake.message}
                  </p>
                ) : null}
              </div>
            ) : null}

            <div>
              <p className="overline text-content-muted">{t('screen.now')}</p>
              <p className="mt-1 break-words font-display text-heading-md text-content-primary">
                {screen.now?.title ?? t('conductor.nothingRunning')}
              </p>
            </div>

            {screen.next ? (
              <div>
                <p className="overline text-content-muted">{t('screen.next')}</p>
                <p className="mt-1 break-words text-content-primary">
                  {screen.next.starts_at ? (
                    <span className="mr-2 font-mono tabular-nums text-champagne-300">
                      {formatTime(screen.next.starts_at, locale, timezone)}
                    </span>
                  ) : null}
                  {screen.next.title}
                </p>
              </div>
            ) : null}

            {screen.later.length > 0 ? (
              <div>
                <p className="overline text-content-muted">{t('screen.later')}</p>
                <ul className="mt-1 space-y-1 text-sm text-content-secondary">
                  {screen.later.map((item) => (
                    <li key={item.id} className="break-words">
                      {item.starts_at ? (
                        <span className="mr-2 font-mono tabular-nums">
                          {formatTime(item.starts_at, locale, timezone)}
                        </span>
                      ) : null}
                      {item.title}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {screen.open_votes.length > 0 || screen.tiebreak_running ? (
              <div className="rounded-md border border-ember-400/40 px-3 py-2">
                <p className="text-caption text-ember-300">
                  {screen.tiebreak_running
                    ? t('conductor.cockpit.screen.tiebreak')
                    : t('conductor.cockpit.screen.votesOpen', {
                        names: screen.open_votes.join(' · '),
                      })}
                </p>
                {(screen.vote_timers ?? []).map((timer) => (
                  <PreviewTimer
                    key={timer.name}
                    name={timer.name}
                    endsAt={timer.ends_at}
                    serverTime={screen.server_time}
                  />
                ))}
              </div>
            ) : null}

            {screen.winners.length > 0 ? (
              <ul className="space-y-1">
                {screen.winners.map((winner) => (
                  <li key={winner.contest} className="break-words text-sm text-champagne-300">
                    {t('conductor.cockpit.screen.winner', {
                      contest: winner.contest,
                      name: winner.name,
                    })}
                  </li>
                ))}
              </ul>
            ) : null}
          </Card>
        ) : null}
      </section>

      <section className="space-y-3">
        <SectionHead
          title={t('conductor.cockpit.screen.links')}
          hint={
            can('event.settings.update')
              ? t('conductor.cockpit.screen.linksHint')
              : t('conductor.cockpit.screen.linkAdminOnly')
          }
        />
        {can('event.settings.update') ? <ScreenLinkCard eventId={eventId} /> : null}
        {programTarget ? (
          <LinkWithQr title={t('conductor.cockpit.screen.programLink')} target={programTarget} />
        ) : null}
      </section>
    </div>
  )
}

/** Le décompte tel que la salle le lit, en petit : ce que la télé affiche. */
function PreviewTimer({
  name,
  endsAt,
  serverTime,
}: {
  name: string
  endsAt: string
  serverTime: string
}) {
  const { t } = useTranslation()
  const remaining = useCountdown(endsAt, serverTime)
  if (remaining === null) return null
  return (
    <p className="mt-1 break-words text-caption text-content-secondary">
      {name} —{' '}
      <span className="font-mono tabular-nums text-content-primary">
        {remaining > 0 ? formatClock(remaining) : t('voteTimer.screenElapsed')}
      </span>
    </p>
  )
}
